import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { DONE, NOT_STARTED } from 'ui-shepherd-react/constants/TourStates';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
const debug = false;
const debugLog = (...args) => {
  if (debug) {
    console.log(...args);
  }
};
const debugWarn = (...args) => {
  if (debug) {
    console.warn(...args);
  }
};
const useDebugProgress = (tourKey, tourEnv) => {
  const {
    tour
  } = useContext(TourContext);
  const activeTour = tour.getTour();
  const step = tour.getStep();
  // @ts-expect-error Property progress does not exist on type ProgressStore
  const progress = tour.getHandler().progressStore.progress;
  useEffect(() => {
    if (debug && tourKey === activeTour) {
      console.log(`Progress for tour ${tourKey} in ${tourEnv}: ${JSON.stringify(progress)}`);
    }
  }, [activeTour, progress, step, tourEnv, tourKey]);
};
const useSyncCurrentStep = (bus, tourEnv, tourKey) => {
  const tourContext = useContext(TourContext);
  const activeTour = tourContext.tour.getTour();
  // TourContext always returns a new object reference, memoizing based on the activeTour
  // (and not depending on the tour object directly here) prevents the effects below from
  // unncessarily adding/removing event listeners and subscribing/unsubscribing to the
  // tour as the user progresses through its steps.
  const tour = useMemo(() => tourContext.tour, [activeTour]); // eslint-disable-line react-hooks/exhaustive-deps
  const stepRef = useRef(NOT_STARTED);
  const hasActivated = useRef(activeTour === tourKey);
  useDebugProgress(tourKey, tourEnv);
  const syncCurrentStep = useCallback(({
    stepKey,
    tourId,
    source
  }) => {
    if (tourId !== tourKey) {
      debugWarn(`Subscribed tourId ${tourId} doesn't match tourKey prop: ${tourKey}`);
      return;
    }
    if (tour.getStep() === stepKey) {
      debugWarn(`Not syncing ${stepKey} in ${tourEnv}, step is already synced.`);
      return;
    }
    if (activeTour === null) {
      debugWarn(`Activating "${tourId}" in ${tourEnv} before syncing current step.`);
      tour.activate(tourId);
    } else if (activeTour !== tourId) {
      console.error(`Another tour "${activeTour}" is already active in ${tourEnv}.`);
      return;
    }
    if ([DONE, NOT_STARTED].includes(stepKey)) {
      debugWarn(`Tour is ${stepKey} in ${source}, finishing tour in ${tourEnv}.`);
      tour.finish();
      return;
    }
    const {
      steps
    } = tour.getConfig();
    const isValidStep = steps.includes(stepKey);
    if (!isValidStep) {
      console.error(`${stepKey} isn't valid step for ${tourId} in ${tourEnv}`);
      return;
    }
    const currentIndex = steps.indexOf(tour.getStep());
    const nextIndex = steps.indexOf(stepKey);
    if (nextIndex < currentIndex) {
      debugLog(`Going back to ${stepKey} for ${tourId} from ${source} to ${tourEnv}.`);
      tour.back();
    } else {
      debugLog(`Syncing step ${stepKey} for ${tourId} from ${source} to ${tourEnv}.`);
      tour.next(stepKey);
    }
  }, [activeTour, tour, tourEnv, tourKey]);
  useEffect(() => {
    if (tour.getTour() !== tourKey) {
      return emptyFunction;
    }
    const [unsubscribe] = tour.subscribe((stepKey, tourId) => {
      if (!tourId || !stepKey || tourId !== tourKey) {
        return;
      }
      if (stepRef.current === stepKey) {
        debugWarn(`Not broadcasting redundant step ${stepKey} from ${tourEnv}`);
        return;
      }
      debugLog(`Sending post:syncTourStep: ${stepKey} from ${tourEnv}`);
      stepRef.current = stepKey;
      const message = {
        action: 'syncTourStep',
        stepKey,
        tourId,
        source: tourEnv
      };
      bus.postMessage(message);
    });
    return unsubscribe;
  }, [bus, tour, tourEnv, tourKey]);
  useEffect(() => {
    if (tour.getTour() !== tourKey) {
      return emptyFunction;
    }
    bus.on('post:syncTourStep', message => {
      const {
        stepKey,
        tourId
      } = message;
      if (tourId !== tourKey) {
        return;
      }
      debugLog(`Received post:syncTourStep ${stepKey} in ${tourEnv}`);
      stepRef.current = stepKey;
      syncCurrentStep(message);
    });
    return () => bus.off('post:syncTourStep');
  }, [bus, tour, tourEnv, tourKey, syncCurrentStep]);
  useEffect(() => {
    if (hasActivated.current) {
      return emptyFunction;
    }
    bus.on('post:syncTourState', message => {
      if (tourKey !== message.tourKey || hasActivated.current || tour.getStep() !== NOT_STARTED) {
        return;
      }
      const {
        isActive,
        source
      } = message;
      debugLog(`Received syncTourState message in ${tourEnv}`, {
        isActive,
        source,
        tourEnv
      });
      if (isActive) {
        hasActivated.current = true;
        tour.activate(tourKey);
      }
    });
    return () => bus.off('post:syncTourState');
  }, [bus, tour, tourEnv, tourKey]);
  useEffect(() => {
    if (hasActivated.current) {
      return emptyFunction;
    }
    const [unsubscribe] = tour.subscribe((stepKey, tourId) => {
      if (!stepKey || !tourId) {
        return;
      }
      if (tourId === tourKey && hasActivated.current) {
        return;
      }
      hasActivated.current = true;
      const message = {
        action: 'syncTourState',
        isActive: hasActivated.current,
        tourKey,
        source: tourEnv
      };
      debugLog(`Broadcasting syncTourState message in ${tourEnv}`, message);
      bus.postMessage(message);
    });
    return unsubscribe;
  }, [bus, tour, tourEnv, tourKey]);
};
export const TourSyncController = ({
  bus,
  tourKey,
  tourEnv
}) => {
  useSyncCurrentStep(bus, tourEnv, tourKey);
  return null;
};